import React, { useState, useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import "@/App.css";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
    Phone,
    Mail,
    MapPin,
    Scissors,
    Leaf,
    Trees,
    Sparkles,
    Wrench,
    Droplets,
    Bug,
    Hammer,
    Lightbulb,
    Snowflake,
    SprayCan,
    Shovel,
    SquareStack,
    CheckCircle2,
    Quote,
    Star,
    ArrowRight,
    Menu,
    X,
    ChevronDown,
    Send,
    Loader2,
    Lock,
    LogOut,
    Trash2,
    RefreshCw,
} from "lucide-react";

const PHONE = "615-766-4254";
const PHONE_TEL = "tel:6157664254";
const EMAIL = "thechosencut@thechosencut.com";
const SERVICE_AREA = "Nashville · Springfield · Clarksville · Surrounding Cities";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/utnclum5_F572CC33-11DF-4778-9D94-4A28A10E9472_4_5005_c.jpeg";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/zococq3s_8C03C859-7E27-4150-B129-B6B644033654.jpeg";
const IMG_MOWING =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/rkh3tcwg_1E448172-01A0-4E8B-A1FC-6059BA6BE4D8.jpeg";
const IMG_HARDSCAPE =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/1ce3nm0w_33B5394A-766F-497D-8DEA-FF06E135D352.jpeg";
const IMG_CLEANUP =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/3ux3a31n_35DA94A1-1F46-4DA7-87F6-6B0A1D42E77C.jpeg";
const IMG_ABOUT =
    "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/lpa4jgcn_F61F354A-6EC1-4938-8128-3DB062CE1E61.jpeg";

/* ---------- Navigation ---------- */
const Nav = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    const links = [
        { href: "#services", label: "Services" },
        { href: "#additional", label: "More" },
        { href: "#gallery", label: "Gallery" },
        { href: "#testimonials", label: "Reviews" },
        { href: "#about", label: "About" },
        { href: "#quote", label: "Quote" },
    ];
    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-brand-cream/90 backdrop-blur-md border-b border-brand-line"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-2"
                >
                    <span
                        className={`font-display text-xl tracking-wide ${
                            scrolled ? "text-brand-charcoal" : "text-brand-cream"
                        }`}
                    >
                        The Chosen Cut
                    </span>
                </a>
                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={`text-xs uppercase tracking-[0.18em] font-medium transition-colors ${
                                scrolled
                                    ? "text-brand-charcoal hover:text-brand-forest"
                                    : "text-brand-cream/90 hover:text-brand-cream"
                            }`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>
                <a
                    href={PHONE_TEL}
                    data-testid="nav-call-btn"
                    className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-medium border transition-all ${
                        scrolled
                            ? "border-brand-forest text-brand-forest hover:bg-brand-forest hover:text-brand-cream"
                            : "border-brand-cream text-brand-cream hover:bg-brand-cream hover:text-brand-forest"
                    }`}
                >
                    <Phone className="h-3.5 w-3.5" /> {PHONE}
                </a>
                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className={`md:hidden ${scrolled ? "text-brand-charcoal" : "text-brand-cream"}`}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden bg-brand-cream border-t border-brand-line"
                >
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className="text-sm uppercase tracking-[0.18em] text-brand-charcoal py-2"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={PHONE_TEL}
                            data-testid="nav-mobile-call-btn"
                            className="btn-forest mt-2"
                        >
                            <Phone className="h-4 w-4" /> Call {PHONE}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

/* ---------- Hero ---------- */
const Hero = () => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
        <div
            className="absolute inset-0 hero-bg"
            style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-28 pt-32">
            <div className="max-w-3xl reveal">
                <div className="overline text-brand-sand mb-6" data-testid="hero-overline">
                    Premium Landscaping · Est. Tennessee
                </div>
                <h1
                    data-testid="hero-title"
                    className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-brand-cream leading-[0.95] tracking-tight font-semibold"
                >
                    The Chosen Cut.
                </h1>
                <p
                    data-testid="hero-motto"
                    className="font-display italic text-2xl sm:text-3xl lg:text-4xl text-brand-sand mt-6 tracking-wide"
                >
                    “We get it done.”
                </p>
                <p
                    data-testid="hero-subtitle"
                    className="text-base sm:text-lg text-brand-cream/85 mt-8 max-w-xl leading-relaxed font-light"
                >
                    A meticulous lawn & landscape crew serving {SERVICE_AREA}.
                    Sharp lines. Honest work. Yards that turn heads.
                </p>
                <div className="flex flex-wrap gap-4 mt-10">
                    <a
                        href="#services"
                        data-testid="hero-cta-services"
                        className="btn-forest bg-brand-cream !text-brand-forest hover:bg-brand-sand hover:!text-brand-forest"
                    >
                        See our work <ArrowRight className="h-4 w-4" />
                    </a>
                    <a
                        href={PHONE_TEL}
                        data-testid="hero-cta-call"
                        className="btn-outline-forest !border-brand-cream !text-brand-cream hover:!bg-brand-cream hover:!text-brand-forest"
                    >
                        <Phone className="h-4 w-4" /> Get a free quote
                    </a>
                </div>
            </div>
        </div>
        <div className="absolute bottom-8 right-6 lg:right-12 z-10 hidden md:flex items-center gap-3 text-brand-cream/80">
            <div className="h-px w-10 bg-brand-cream/50" />
            <span className="overline !text-brand-cream/70">Scroll</span>
            <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
    </section>
);

/* ---------- Standard Services ---------- */
const STANDARD = [
    {
        title: "Lawn Mowing",
        desc: "Sharp, even cuts at the right height for your turf — striped to perfection.",
        icon: Scissors,
        large: true,
        image: IMG_MOWING,
    },
    {
        title: "Weed-Eating",
        desc: "Crisp trim along fences, beds, posts and trees.",
        icon: Sparkles,
    },
    {
        title: "Edging",
        desc: "Razor-clean lines between turf, walks and beds.",
        icon: SquareStack,
    },
    {
        title: "Branch & Leaf Cleanup",
        desc: "Hauled away — driveways, lawns and beds left tidy.",
        icon: Leaf,
    },
    {
        title: "Garden Weed Removal",
        desc: "Hand-pulled and treated so beds stay tidy longer.",
        icon: Trees,
    },
    {
        title: "Leaf Blowing",
        desc: "Driveways, walks and patios cleared off before we leave.",
        icon: Sparkles,
    },
];

const StandardServices = () => (
    <section
        id="services"
        data-testid="standard-services-section"
        className="bg-brand-cream py-20 lg:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-10 mb-14 lg:mb-20">
                <div className="lg:col-span-5">
                    <div className="overline mb-4" data-testid="standard-overline">
                        01 — Standard Service Package
                    </div>
                    <h2 className="font-display text-4xl lg:text-5xl text-brand-charcoal leading-tight tracking-tight">
                        Every visit, every yard — done right.
                    </h2>
                </div>
                <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                    <p className="text-base lg:text-lg text-brand-stone leading-relaxed font-light">
                        The five essentials we never skip. One flat package, no
                        surprises — your lawn comes back sharper than the day
                        we left it.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-line border border-brand-line stagger">
                {/* Big feature card */}
                <article
                    data-testid="service-card-mowing"
                    className="md:col-span-2 lg:row-span-2 lg:col-span-2 bg-white relative overflow-hidden group min-h-[360px] lg:min-h-[520px]"
                >
                    <img
                        src={STANDARD[0].image}
                        alt="Lawn mowing"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="relative h-full p-8 lg:p-12 flex flex-col justify-end">
                        <div className="overline !text-brand-sand mb-4">Signature</div>
                        <h3 className="font-display text-3xl lg:text-5xl text-brand-cream tracking-tight">
                            {STANDARD[0].title}
                        </h3>
                        <p className="text-brand-cream/85 mt-3 max-w-md font-light leading-relaxed">
                            {STANDARD[0].desc}
                        </p>
                    </div>
                </article>

                {STANDARD.slice(1).map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <article
                            key={s.title}
                            data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className="bg-white p-8 lg:p-10 flex flex-col gap-4 transition-all duration-300 hover:bg-brand-forest hover:text-brand-cream group min-h-[220px]"
                        >
                            <Icon
                                className="h-7 w-7 text-brand-forest group-hover:text-brand-sand transition-colors"
                                strokeWidth={1.4}
                            />
                            <h3 className="font-display text-2xl text-brand-charcoal group-hover:text-brand-cream transition-colors">
                                {s.title}
                            </h3>
                            <p className="text-sm text-brand-stone group-hover:text-brand-cream/85 transition-colors leading-relaxed font-light">
                                {s.desc}
                            </p>
                            <div className="mt-auto pt-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-moss group-hover:text-brand-sand transition-colors">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Included
                            </div>
                        </article>
                    );
                })}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-brand-line pt-8">
                <p className="text-sm text-brand-stone font-light">
                    Weekly · Bi-weekly · Monthly schedules available.
                </p>
                <a
                    href={PHONE_TEL}
                    data-testid="standard-cta-quote"
                    className="btn-forest"
                >
                    Get my flat-rate quote <ArrowRight className="h-4 w-4" />
                </a>
            </div>
        </div>
    </section>
);

/* ---------- Additional Services ---------- */
const ADDITIONAL = [
    { title: "Tree Pruning", icon: Scissors },
    { title: "Tree Removal", icon: Trees },
    { title: "Stump Grinding", icon: Hammer },
    { title: "Hardscaping/Landscaping", icon: SquareStack },
    { title: "Stone Building", icon: SquareStack },
    { title: "Mulch Installation", icon: Shovel },
    { title: "Sod Installation", icon: Shovel },
    { title: "Irrigation", icon: Droplets },
    { title: "Fertilization", icon: Leaf },
    { title: "Weed Control", icon: Sparkles },
    { title: "Trimming", icon: Scissors },
    { title: "Full Cleanup", subtitle: "Seasonal & property", icon: Leaf },
    { title: "Junk Removal", icon: Trash2 },
    { title: "Pressure Washing", icon: SprayCan },
    { title: "Custom Projects", icon: Wrench },
];

const AdditionalServices = () => (
    <section
        id="additional"
        data-testid="additional-services-section"
        className="bg-white py-20 lg:py-32 border-y border-brand-line"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-10 mb-14">
                <div className="lg:col-span-7">
                    <div className="overline mb-4">02 — Additional Services</div>
                    <h2 className="font-display text-4xl lg:text-5xl text-brand-charcoal leading-tight tracking-tight">
                        Beyond the cut.
                        <br />
                        <span className="italic text-brand-moss">Built around your property.</span>
                    </h2>
                </div>
                <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                    <p className="text-base lg:text-lg text-brand-stone leading-relaxed font-light">
                        From a single tree pruning to seasonal lighting and full
                        hardscape builds — bundle as needed.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-brand-line border border-brand-line">
                {ADDITIONAL.map((s) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={s.title}
                            data-testid={`additional-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="bg-white p-6 lg:p-8 flex flex-col items-start gap-3 transition-all duration-300 hover:bg-brand-cream group cursor-default"
                        >
                            <Icon
                                className="h-6 w-6 text-brand-moss group-hover:text-brand-forest transition-colors"
                                strokeWidth={1.4}
                            />
                            <p className="font-display text-lg lg:text-xl text-brand-charcoal leading-snug">
                                {s.title}
                            </p>
                            {s.subtitle && (
                                <p className="text-xs text-brand-stone -mt-1 font-light">
                                    {s.subtitle}
                                </p>
                            )}
                            <span className="overline mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                Available →
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
);

/* ---------- Gallery (Before/After) ---------- */
const GALLERY = {
    mowing: {
        label: "Lawn Mowing",
        photos: [
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/eaq2u33b_425BE07E-0C7E-4348-86E7-F24986156FEE.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/o5r5xdsv_B7C4D5B2-D7E0-4EE6-B590-FF1EA4CE8007.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/aj71d35d_ACEE6BAD-EF00-4D32-AEF4-6778E108859C.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/85qy98qv_E08CDA94-A7A0-4891-B127-614BF33D3655.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/tem0ziku_030E455A-71D1-438D-85BD-BAFE57B865FF.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/ifgv4vc4_FB5330DD-C28E-4C46-83B6-6CFBC38CB024.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/r4mqs34c_F61F354A-6EC1-4938-8128-3DB062CE1E61.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/l62ddm20_15034E6C-7E2E-4E5E-A722-269CA1315552.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/1q7gm4xk_D6259CDB-026B-4B86-B0A0-9D9C574FDB06.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/v72ysw23_0CDC47CD-4E3E-4170-B8DA-CA847E31EFE3.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/lpa4jgcn_F61F354A-6EC1-4938-8128-3DB062CE1E61.jpeg",
        ],
    },
    weeding: {
        label: "Weed-Eating & Edging",
        photos: [],
    },
    cleanup: {
        label: "Branch & Leaf Cleanup",
        photos: [
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/3ux3a31n_35DA94A1-1F46-4DA7-87F6-6B0A1D42E77C.jpeg",
        ],
    },
    hardscape: {
        label: "Hardscaping/Landscaping",
        photos: [
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/1ce3nm0w_33B5394A-766F-497D-8DEA-FF06E135D352.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/5nv9yzbv_608F6F4E-FD3C-4DEF-ADE3-3A380008D597.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/88q1a9de_0F5599F0-6FA0-4842-AE6C-60615017C94B.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/hbltsqoe_8787B03B-EBAB-458F-92AC-9D0EBDDEEFED.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/72o5ugim_C9BD805B-DDCD-42BF-B36A-87DFFD59907C.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/ycz45pzt_97C05D5A-7DF7-497C-9CB6-5B1A6EF3DBE5.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/kc2gj9qt_820F2CF2-B343-45D4-9B10-8AB0B581A0E1.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/pc6hllwh_D43450CC-88DA-4F59-8EDB-19806774DC0C.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/o3ha3g02_76A12110-E03C-4E7A-87CA-1473CB7EC27C.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/rlk0w1ln_4D4758D8-4038-4EA1-A115-020D64D96AF2.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/xizssbj7_6DF3462B-474C-4DC2-9F16-3B278C576050.jpeg",
        ],
    },
    sod: {
        label: "Sod Installation",
        photos: [
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/lkqdvq0l_3E5D8407-0D80-4425-AF50-29C4C1F3B712.jpeg",
        ],
    },
    pressure: {
        label: "Pressure Washing",
        photos: [
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/0r85pfse_2D6FEF2F-D90A-4398-A775-0BC37AC05782_4_5005_c.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/m5i6i99c_DE198541-3394-4670-ADE3-56CCD285A413_4_5005_c.jpeg",
            "https://customer-assets.emergentagent.com/job_chosen-cut-pro/artifacts/y65kybc7_4BF92A2C-D24C-4181-A884-847B37E6F97D_1_201_a.jpeg",
        ],
    },
};

const Gallery = () => {
    const keys = Object.keys(GALLERY);
    const [active, setActive] = useState(keys[0]);
    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="bg-brand-cream py-20 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-7">
                        <div className="overline mb-4">03 — Gallery</div>
                        <h2 className="font-display text-4xl lg:text-5xl text-brand-charcoal leading-tight tracking-tight">
                            Real yards.
                            <br />
                            <span className="italic text-brand-moss">Real work.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                        <p className="text-base lg:text-lg text-brand-stone leading-relaxed font-light">
                            Tap a service to see photos from properties we've
                            handled across Middle Tennessee.
                        </p>
                    </div>
                </div>

                <Tabs value={active} onValueChange={setActive} className="w-full">
                    <TabsList
                        data-testid="gallery-tabs"
                        className="w-full bg-transparent p-0 h-auto flex flex-wrap gap-x-8 gap-y-2 justify-start border-b border-brand-line rounded-none mb-10"
                    >
                        {keys.map((k) => (
                            <TabsTrigger
                                key={k}
                                value={k}
                                data-testid={`gallery-tab-${k}`}
                                className="tab-trigger relative px-0 py-4 text-xs uppercase tracking-[0.18em] font-medium text-brand-stone data-[state=active]:shadow-none rounded-none bg-transparent transition-colors"
                            >
                                {GALLERY[k].label}
                                {GALLERY[k].photos.length > 0 && (
                                    <span className="ml-2 text-[10px] text-brand-moss/70">
                                        {GALLERY[k].photos.length}
                                    </span>
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {keys.map((k) => {
                        const photos = GALLERY[k].photos;
                        return (
                            <TabsContent
                                key={k}
                                value={k}
                                className="mt-0 focus-visible:outline-none"
                            >
                                {photos.length === 0 ? (
                                    <div
                                        data-testid={`gallery-empty-${k}`}
                                        className="border border-dashed border-brand-line bg-white/60 py-20 lg:py-28 px-6 text-center"
                                    >
                                        <div className="overline !text-brand-moss mb-3">
                                            Coming soon
                                        </div>
                                        <p className="font-display text-2xl lg:text-3xl text-brand-charcoal">
                                            New {GALLERY[k].label.toLowerCase()} photos on the way.
                                        </p>
                                        <p className="mt-3 text-brand-stone font-light max-w-md mx-auto">
                                            We're rolling these out as the season picks up — check back soon.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                        {photos.map((src, idx) => (
                                            <figure
                                                key={src + idx}
                                                data-testid={`gallery-photo-${k}-${idx}`}
                                                className="ba-card aspect-square overflow-hidden group cursor-zoom-in"
                                            >
                                                <img
                                                    src={src}
                                                    alt={`${GALLERY[k].label} ${idx + 1}`}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                                />
                                            </figure>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>
                        );
                    })}
                </Tabs>

                <p className="mt-10 text-xs uppercase tracking-[0.18em] text-brand-stone/70">
                    Real photos from {SERVICE_AREA.split(" · ").slice(0, 3).join(", ")}.
                </p>
            </div>
        </section>
    );
};

/* ---------- Testimonials ---------- */
const TESTIMONIALS = [
    {
        name: "Mark",
        location: "Nashville, TN",
        text: "Great work and even better people. The crew was polite, easy to talk to, and treated my place like it was their own. I'll be using them every season.",
    },
    {
        name: "Carol Wyne",
        location: "Springfield, TN",
        text: "What I appreciated most was how friendly and down-to-earth they were. The yard came out beautiful and they made the whole thing feel simple.",
    },
    {
        name: "Mrs. Barbara",
        location: "Clarksville, TN",
        text: "Such kind, respectful young men. They listened to what I wanted, did exactly that, and left everything spotless. Truly a pleasure to work with.",
    },
    {
        name: "Richard",
        location: "Springfield, TN",
        text: "Honest pricing, on-time, and the work was top notch. Easy to get a hold of and easy to deal with — that's hard to find anymore.",
    },
    {
        name: "Bill",
        location: "Nashville, TN",
        text: "Felt more like having neighbors over than hiring a service. Friendly from the first call to the final wave goodbye, and the yard looks fantastic.",
    },
];

const Testimonials = () => (
    <section
        id="testimonials"
        data-testid="testimonials-section"
        className="bg-brand-forest py-20 lg:py-32 relative overflow-hidden"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
            <div className="grid lg:grid-cols-12 gap-10 mb-14">
                <div className="lg:col-span-7">
                    <div className="overline !text-brand-sand mb-4">04 — Testimonials</div>
                    <h2 className="font-display text-4xl lg:text-5xl text-brand-cream leading-tight tracking-tight">
                        Friendly faces.
                        <br />
                        <span className="italic text-brand-sand">Honest work.</span>
                    </h2>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-px bg-brand-moss/40 border border-brand-moss/40">
                {TESTIMONIALS.map((t, i) => (
                    <article
                        key={t.name}
                        data-testid={`testimonial-${i}`}
                        className="bg-brand-forest p-8 lg:p-12 flex flex-col gap-6 transition-colors duration-500 hover:bg-[#193322]"
                    >
                        <Quote
                            className="h-8 w-8 text-brand-sand"
                            strokeWidth={1}
                        />
                        <p className="font-display italic text-xl lg:text-2xl text-brand-cream leading-snug">
                            “{t.text}”
                        </p>
                        <div className="mt-auto pt-6 border-t border-brand-moss/40 flex items-center justify-between">
                            <div>
                                <p className="text-brand-cream font-medium">{t.name}</p>
                                <p className="text-brand-sand text-sm">{t.location}</p>
                            </div>
                            <div className="flex items-center gap-1 text-brand-sand">
                                {[...Array(5)].map((_, j) => (
                                    <Star
                                        key={j}
                                        className="h-4 w-4 fill-brand-sand"
                                        strokeWidth={0}
                                    />
                                ))}
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- About Us ---------- */
const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="bg-brand-cream py-20 lg:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
                <div className="overline mb-4">05 — About Us</div>
                <h2 className="font-display text-4xl lg:text-5xl text-brand-charcoal leading-tight tracking-tight mb-8">
                    A small crew with a&nbsp;
                    <span className="italic text-brand-moss">big standard.</span>
                </h2>
                <div className="space-y-5 text-base lg:text-lg text-brand-stone leading-relaxed font-light">
                    <p>
                        The Chosen Cut is a Tennessee-based landscaping crew
                        built on three things: showing up, doing the work, and
                        leaving the property better than we found it.
                    </p>
                    <p>
                        We treat every yard like it's the only one on the
                        schedule. No corner cuts, no skipped edges, no excuses
                        — just clean lines, healthy turf, and a property you're
                        proud to pull up to.
                    </p>
                    <p>
                        Our morals and values are what shape how we work and
                        how we treat people. Be kind. Be honest. Give it
                        everything you've got, every single time. That's the
                        standard — and it's why our customers feel less like
                        clients and more like neighbors.
                    </p>
                </div>
                <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                    <div>
                        <p className="font-display text-3xl text-brand-forest">500+</p>
                        <p className="overline mt-1">Yards served</p>
                    </div>
                    <div>
                        <p className="font-display text-3xl text-brand-forest">4</p>
                        <p className="overline mt-1">Cities covered</p>
                    </div>
                    <div>
                        <p className="font-display text-3xl text-brand-forest">100%</p>
                        <p className="overline mt-1">Satisfaction</p>
                    </div>
                </div>
                <div className="mt-10 flex items-center gap-3 text-brand-charcoal">
                    <MapPin className="h-5 w-5 text-brand-moss" strokeWidth={1.5} />
                    <p className="text-sm font-medium">{SERVICE_AREA}</p>
                </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2 relative">
                <div className="aspect-[4/5] overflow-hidden border border-brand-line">
                    <img
                        src={IMG_ABOUT}
                        alt="Our crew at work"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-brand-forest text-brand-cream p-6 lg:p-8 max-w-xs hidden md:block">
                    <p className="font-display italic text-lg lg:text-xl leading-snug">
                        “Treat every yard like it's the only one on the schedule.”
                    </p>
                    <p className="overline !text-brand-sand mt-3">— The Chosen Cut crew</p>
                </div>
            </div>
        </div>

        {/* Where our standards come from */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 lg:mt-28">
            <div className="border-t border-brand-line pt-12 lg:pt-16">
                <div className="overline mb-3" data-testid="about-scripture-overline">
                    Where our standards come from
                </div>
                <h3 className="font-display text-3xl lg:text-4xl text-brand-charcoal leading-tight tracking-tight max-w-3xl">
                    Two verses we keep on the dashboard.
                    <br />
                    <span className="italic text-brand-moss">They shape every job.</span>
                </h3>
                <p className="mt-6 text-base lg:text-lg text-brand-stone font-light max-w-2xl leading-relaxed">
                    Faith isn't the service we sell — but it's the reason we
                    show up on time, charge fair, work hard, and treat every
                    customer like family.
                </p>
            </div>
            <div
                data-testid="about-scripture"
                className="mt-10 grid md:grid-cols-2 gap-px bg-brand-line border border-brand-line"
            >
                <div className="bg-brand-forest text-brand-cream p-8 lg:p-10">
                    <p className="overline !text-brand-sand mb-3">Ephesians 4:32</p>
                    <p className="font-display italic text-xl lg:text-2xl leading-snug">
                        “Be kind to one another, tenderhearted, forgiving one
                        another, as God in Christ forgave you.”
                    </p>
                </div>
                <div className="bg-brand-forest text-brand-cream p-8 lg:p-10">
                    <p className="overline !text-brand-sand mb-3">Colossians 3:23</p>
                    <p className="font-display italic text-xl lg:text-2xl leading-snug">
                        “Whatever you do, work heartily, as for the Lord and
                        not for men.”
                    </p>
                </div>
            </div>
        </div>
    </section>
);

/* ---------- Quote Request Form ---------- */
const SERVICE_OPTIONS = [
    "Standard Service Package (Mowing + Trim)",
    "Lawn Mowing Only",
    "Trimming",
    "Weed Control",
    "Branch & Leaf Cleanup",
    "Full Cleanup (Seasonal & Property)",
    "Hardscaping / Landscaping",
    "Stone Building",
    "Mulch Installation",
    "Tree Pruning / Removal",
    "Stump Grinding",
    "Sod Installation",
    "Pressure Washing",
    "Junk Removal",
    "Other / Not sure yet",
];

const QuoteForm = () => {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        service: SERVICE_OPTIONS[0],
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Please share your name.");
            return;
        }
        if (!form.phone.trim() && !form.email.trim()) {
            toast.error("Please share a phone number or email so we can reach you.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/quote`, form);
            setSubmitted(true);
            toast.success("Quote request received — we'll be in touch soon.");
            setForm({
                name: "",
                phone: "",
                email: "",
                address: "",
                service: SERVICE_OPTIONS[0],
                message: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please call us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="quote"
            data-testid="quote-section"
            className="bg-white py-20 lg:py-32 border-t border-brand-line"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-5">
                    <div className="overline mb-4">06 — Free Quote</div>
                    <h2 className="font-display text-4xl lg:text-5xl text-brand-charcoal leading-tight tracking-tight">
                        Tell us about
                        <br />
                        <span className="italic text-brand-moss">your property.</span>
                    </h2>
                    <p className="mt-6 text-base lg:text-lg text-brand-stone leading-relaxed font-light max-w-md">
                        A few quick details and we'll get back to you with a
                        no-pressure quote — usually the same day.
                    </p>
                    <div className="mt-10 space-y-4">
                        <a
                            href={PHONE_TEL}
                            data-testid="quote-direct-call"
                            className="btn-forest w-full sm:w-auto"
                        >
                            <Phone className="h-4 w-4" /> Or call {PHONE}
                        </a>
                        <p className="text-xs text-brand-stone/80 font-light max-w-md">
                            Prefer to talk? We pick up — no phone trees, no
                            voicemail mazes.
                        </p>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {submitted ? (
                        <div
                            data-testid="quote-success"
                            className="border border-brand-line bg-brand-cream p-8 lg:p-12"
                        >
                            <CheckCircle2
                                className="h-10 w-10 text-brand-forest mb-4"
                                strokeWidth={1.4}
                            />
                            <h3 className="font-display text-2xl lg:text-3xl text-brand-charcoal mb-3">
                                Thanks — we got it.
                            </h3>
                            <p className="text-brand-stone font-light leading-relaxed">
                                We'll reach out shortly to walk through your
                                property and put a price on the work. If it's
                                urgent, give us a ring at{" "}
                                <a
                                    href={PHONE_TEL}
                                    className="text-brand-forest font-medium underline-offset-2 hover:underline"
                                >
                                    {PHONE}
                                </a>
                                .
                            </p>
                            <button
                                onClick={() => setSubmitted(false)}
                                data-testid="quote-send-another"
                                className="mt-6 overline text-brand-moss hover:text-brand-forest"
                            >
                                ← Send another request
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="quote-form"
                            className="border border-brand-line bg-brand-cream p-6 sm:p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
                        >
                            <div className="sm:col-span-1 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-name">
                                    Name
                                </label>
                                <input
                                    id="q-name"
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={update("name")}
                                    data-testid="quote-input-name"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors"
                                    placeholder="Your full name"
                                />
                            </div>
                            <div className="sm:col-span-1 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-phone">
                                    Phone <span className="text-brand-stone/60 normal-case tracking-normal">(or email)</span>
                                </label>
                                <input
                                    id="q-phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    data-testid="quote-input-phone"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors"
                                    placeholder="(615) 555-0123"
                                />
                            </div>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-email">
                                    Email <span className="text-brand-stone/60 normal-case tracking-normal">(or phone)</span>
                                </label>
                                <input
                                    id="q-email"
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    data-testid="quote-input-email"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-address">
                                    Property Address
                                </label>
                                <input
                                    id="q-address"
                                    type="text"
                                    value={form.address}
                                    onChange={update("address")}
                                    data-testid="quote-input-address"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors"
                                    placeholder="Street, city"
                                />
                            </div>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-service">
                                    Service
                                </label>
                                <select
                                    id="q-service"
                                    value={form.service}
                                    onChange={update("service")}
                                    data-testid="quote-input-service"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors appearance-none"
                                >
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                <label className="overline" htmlFor="q-message">
                                    Anything we should know? (optional)
                                </label>
                                <textarea
                                    id="q-message"
                                    rows={4}
                                    value={form.message}
                                    onChange={update("message")}
                                    data-testid="quote-input-message"
                                    className="bg-white border border-brand-line px-4 py-3 text-sm text-brand-charcoal focus:outline-none focus:border-brand-forest transition-colors resize-none"
                                    placeholder="Yard size, gate access, dog in the back, ideal day..."
                                />
                            </div>
                            <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2">
                                <p className="text-xs text-brand-stone/80 font-light">
                                    By submitting, you agree to be contacted about your quote.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="quote-submit-btn"
                                    className="btn-forest disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" />
                                            Request my quote
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ---------- CTA Strip ---------- */
const CTAStrip = () => (
    <section
        data-testid="cta-strip"
        className="bg-brand-charcoal py-16 lg:py-20"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
                <div className="overline !text-brand-sand mb-3">Ready when you are</div>
                <h3 className="font-display text-3xl lg:text-4xl text-brand-cream leading-tight">
                    Free quote, same-week scheduling.
                </h3>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row gap-3 lg:justify-end">
                <a
                    href={PHONE_TEL}
                    data-testid="cta-strip-call"
                    className="btn-forest !bg-brand-sand !text-brand-forest hover:!bg-brand-cream"
                >
                    <Phone className="h-4 w-4" /> {PHONE}
                </a>
                <a
                    href={`mailto:${EMAIL}`}
                    data-testid="cta-strip-email"
                    className="btn-outline-forest !border-brand-cream !text-brand-cream hover:!bg-brand-cream hover:!text-brand-charcoal"
                >
                    <Mail className="h-4 w-4" /> Email us
                </a>
            </div>
        </div>
    </section>
);

/* ---------- Footer ---------- */
const Footer = () => (
    <footer
        data-testid="site-footer"
        className="bg-brand-cream border-t border-brand-line"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-3 gap-10">
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <Scissors
                        className="h-5 w-5 text-brand-forest"
                        strokeWidth={1.5}
                    />
                    <span className="font-display text-lg text-brand-charcoal">
                        The Chosen Cut
                    </span>
                </div>
                <p className="font-display italic text-brand-moss">
                    “We get it done.”
                </p>
            </div>
            <div>
                <p className="overline mb-4">Contact</p>
                <ul className="space-y-3 text-sm text-brand-charcoal">
                    <li>
                        <a
                            href={PHONE_TEL}
                            data-testid="footer-phone"
                            className="flex items-center gap-2 hover:text-brand-forest transition-colors"
                        >
                            <Phone className="h-4 w-4 text-brand-moss" />
                            {PHONE}
                        </a>
                    </li>
                    <li>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="footer-email"
                            className="flex items-center gap-2 hover:text-brand-forest transition-colors break-all"
                        >
                            <Mail className="h-4 w-4 text-brand-moss flex-shrink-0" />
                            {EMAIL}
                        </a>
                    </li>
                    <li className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-brand-moss mt-0.5 flex-shrink-0" />
                        <span>{SERVICE_AREA}</span>
                    </li>
                </ul>
            </div>
            <div>
                <p className="overline mb-4">Hours</p>
                <ul className="space-y-2 text-sm text-brand-charcoal">
                    <li className="flex justify-between border-b border-brand-line pb-2">
                        <span>Mon — Fri</span>
                        <span className="text-brand-stone">7am — 7pm</span>
                    </li>
                    <li className="flex justify-between border-b border-brand-line pb-2">
                        <span>Saturday</span>
                        <span className="text-brand-stone">8am — 5pm</span>
                    </li>
                    <li className="flex justify-between">
                        <span>Sunday</span>
                        <span className="text-brand-stone">By appointment</span>
                    </li>
                </ul>
            </div>
        </div>
        <div className="border-t border-brand-line bg-brand-forest text-brand-cream">
            <div
                data-testid="footer-scripture"
                className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-12 grid md:grid-cols-2 gap-8 md:gap-12"
            >
                <div className="border-l-2 border-brand-sand pl-5">
                    <p className="overline !text-brand-sand mb-2">Ephesians 4:32</p>
                    <p className="font-display italic text-base lg:text-lg leading-relaxed text-brand-cream/95">
                        “Be kind to one another, tenderhearted, forgiving one
                        another, as God in Christ forgave you.”
                    </p>
                </div>
                <div className="border-l-2 border-brand-sand pl-5">
                    <p className="overline !text-brand-sand mb-2">Colossians 3:23</p>
                    <p className="font-display italic text-base lg:text-lg leading-relaxed text-brand-cream/95">
                        “Whatever you do, work heartily, as for the Lord and not
                        for men.”
                    </p>
                </div>
            </div>
        </div>
        <div className="border-t border-brand-line">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-brand-stone">
                <p>© {new Date().getFullYear()} The Chosen Cut. All rights reserved.</p>
                <p className="overline !text-brand-stone/70">Tennessee · Family operated</p>
            </div>
        </div>
    </footer>
);

/* ---------- Floating Call Button ---------- */
const FloatingCall = () => {
    const [open, setOpen] = useState(false);

    const copyPhone = async () => {
        try {
            await navigator.clipboard.writeText(PHONE);
            toast.success("Phone copied to clipboard");
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <>
            {open && (
                <div
                    data-testid="floating-contact-card"
                    className="fixed bottom-32 sm:bottom-28 right-4 sm:right-6 z-[60] w-[280px] sm:w-[320px] bg-white border border-brand-line shadow-2xl reveal"
                >
                    <div className="bg-brand-forest text-brand-cream p-5 flex items-center justify-between">
                        <div>
                            <p className="overline !text-brand-sand">Get in touch</p>
                            <p className="font-display text-xl mt-1">The Chosen Cut</p>
                        </div>
                        <button
                            data-testid="floating-close-btn"
                            onClick={() => setOpen(false)}
                            className="text-brand-cream/80 hover:text-brand-cream"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="p-5 space-y-3">
                        <a
                            href={PHONE_TEL}
                            data-testid="floating-card-call"
                            className="flex items-center justify-between gap-3 p-3 border border-brand-line hover:bg-brand-cream transition-colors"
                        >
                            <span className="flex items-center gap-3 text-brand-charcoal">
                                <Phone className="h-4 w-4 text-brand-moss" />
                                <span className="text-sm font-medium">{PHONE}</span>
                            </span>
                            <span className="overline text-brand-moss">Call</span>
                        </a>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="floating-card-email"
                            className="flex items-center justify-between gap-3 p-3 border border-brand-line hover:bg-brand-cream transition-colors"
                        >
                            <span className="flex items-center gap-3 text-brand-charcoal min-w-0">
                                <Mail className="h-4 w-4 text-brand-moss flex-shrink-0" />
                                <span className="text-xs sm:text-sm font-medium truncate">
                                    {EMAIL}
                                </span>
                            </span>
                            <span className="overline text-brand-moss flex-shrink-0">Email</span>
                        </a>
                        <button
                            onClick={copyPhone}
                            data-testid="floating-copy-phone"
                            className="w-full flex items-center justify-between gap-3 p-3 border border-brand-line hover:bg-brand-cream transition-colors text-left"
                        >
                            <span className="flex items-center gap-3 text-brand-charcoal">
                                <SquareStack className="h-4 w-4 text-brand-moss" />
                                <span className="text-sm font-medium">Copy phone</span>
                            </span>
                            <span className="overline text-brand-moss">Copy</span>
                        </button>
                        <div className="pt-3 border-t border-brand-line text-xs text-brand-stone leading-relaxed">
                            <span className="block">{SERVICE_AREA}</span>
                            <span className="block mt-1">Mon–Sat · Free quotes</span>
                        </div>
                    </div>
                </div>
            )}
            <div className="fixed bottom-20 sm:bottom-16 right-4 sm:right-6 z-[55] flex flex-col items-end gap-3">
                <a
                    href={PHONE_TEL}
                    data-testid="floating-call-btn"
                    aria-label={`Call ${PHONE}`}
                    className="fab-pulse relative h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-brand-forest text-brand-cream flex items-center justify-center shadow-2xl hover:bg-brand-forestHover transition-all duration-300 hover:-translate-y-0.5"
                >
                    <Phone className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.6} />
                </a>
                <button
                    onClick={() => setOpen(!open)}
                    data-testid="floating-contact-toggle"
                    aria-label="Toggle contact info"
                    className="px-4 py-2 rounded-full bg-white border border-brand-line text-brand-charcoal text-xs uppercase tracking-[0.16em] font-medium shadow-md hover:bg-brand-cream transition-all"
                >
                    {open ? "Close" : "Contact"}
                </button>
            </div>
        </>
    );
};

/* ---------- Admin Page ---------- */
const AdminPage = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState(
        sessionStorage.getItem("tcc_admin_pw") || ""
    );
    const [authed, setAuthed] = useState(
        Boolean(sessionStorage.getItem("tcc_admin_pw"))
    );
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [error, setError] = useState("");

    const fetchQuotes = async (pw) => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API}/admin/quotes`, {
                headers: { "X-Admin-Password": pw },
            });
            setQuotes(res.data || []);
            setAuthed(true);
            sessionStorage.setItem("tcc_admin_pw", pw);
        } catch (err) {
            if (err?.response?.status === 401) {
                setError("Invalid password.");
                setAuthed(false);
                sessionStorage.removeItem("tcc_admin_pw");
            } else {
                setError("Could not load quotes. Try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const stored = sessionStorage.getItem("tcc_admin_pw");
        if (stored) fetchQuotes(stored);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLogin = (e) => {
        e.preventDefault();
        if (!password.trim()) return;
        fetchQuotes(password);
    };

    const onLogout = () => {
        sessionStorage.removeItem("tcc_admin_pw");
        setAuthed(false);
        setPassword("");
        setQuotes([]);
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this quote request? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/admin/quotes/${id}`, {
                headers: { "X-Admin-Password": password },
            });
            setQuotes((qs) => qs.filter((q) => q.id !== id));
            toast.success("Quote deleted");
        } catch {
            toast.error("Could not delete");
        }
    };

    const fmtDate = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
                <form
                    onSubmit={onLogin}
                    data-testid="admin-login-form"
                    className="w-full max-w-md bg-white border border-brand-line p-8 lg:p-10"
                >
                    <Link
                        to="/"
                        data-testid="admin-back-home"
                        className="overline text-brand-moss hover:text-brand-forest"
                    >
                        ← Back to site
                    </Link>
                    <div className="mt-6 flex items-center gap-3">
                        <Lock className="h-6 w-6 text-brand-forest" strokeWidth={1.4} />
                        <h1 className="font-display text-3xl text-brand-charcoal">
                            Admin
                        </h1>
                    </div>
                    <p className="mt-3 text-sm text-brand-stone font-light">
                        Enter your password to view quote requests.
                    </p>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="admin-password-input"
                        autoFocus
                        className="mt-6 w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-brand-forest"
                        placeholder="Password"
                    />
                    {error && (
                        <p
                            data-testid="admin-login-error"
                            className="mt-3 text-sm text-red-700"
                        >
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="btn-forest w-full mt-6 disabled:opacity-60"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" /> Checking...
                            </>
                        ) : (
                            <>Sign in</>
                        )}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-cream">
            <header className="bg-brand-forest text-brand-cream">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Lock className="h-5 w-5 text-brand-sand" strokeWidth={1.4} />
                        <div>
                            <p className="overline !text-brand-sand">Admin</p>
                            <h1 className="font-display text-2xl mt-0.5">Quote Requests</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => fetchQuotes(password)}
                            data-testid="admin-refresh-btn"
                            className="inline-flex items-center gap-2 border border-brand-cream/40 px-3 py-2 text-xs uppercase tracking-[0.14em] hover:bg-brand-cream hover:text-brand-forest transition-colors"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="admin-home-btn"
                            className="inline-flex items-center gap-2 border border-brand-cream/40 px-3 py-2 text-xs uppercase tracking-[0.14em] hover:bg-brand-cream hover:text-brand-forest transition-colors"
                        >
                            Site
                        </button>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-2 border border-brand-cream/40 px-3 py-2 text-xs uppercase tracking-[0.14em] hover:bg-brand-cream hover:text-brand-forest transition-colors"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="overline mb-2">Total</p>
                        <p className="font-display text-4xl text-brand-charcoal">
                            {quotes.length}
                        </p>
                    </div>
                    <p className="text-sm text-brand-stone font-light">
                        Latest first. Notification emails are sent to{" "}
                        <span className="text-brand-forest font-medium">{EMAIL}</span>.
                    </p>
                </div>

                {loading && quotes.length === 0 ? (
                    <div className="py-20 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-forest" />
                    </div>
                ) : quotes.length === 0 ? (
                    <div className="border border-dashed border-brand-line bg-white py-20 text-center">
                        <p className="font-display text-2xl text-brand-charcoal">
                            No quote requests yet.
                        </p>
                        <p className="text-brand-stone mt-2 font-light">
                            New submissions will show up here automatically.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
                        {quotes.map((q) => (
                            <article
                                key={q.id}
                                data-testid={`admin-quote-${q.id}`}
                                className="bg-white p-6 lg:p-8 flex flex-col gap-4"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="overline">{fmtDate(q.created_at)}</p>
                                        <h2 className="font-display text-2xl text-brand-charcoal mt-1">
                                            {q.name}
                                        </h2>
                                    </div>
                                    <button
                                        onClick={() => onDelete(q.id)}
                                        data-testid={`admin-delete-${q.id}`}
                                        className="text-brand-stone hover:text-red-700 transition-colors p-2"
                                        aria-label="Delete"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="space-y-2 text-sm">
                                    {q.phone && (
                                        <a
                                            href={`tel:${q.phone.replace(/\D/g, "")}`}
                                            className="flex items-center gap-2 text-brand-charcoal hover:text-brand-forest"
                                        >
                                            <Phone className="h-4 w-4 text-brand-moss" />
                                            {q.phone}
                                        </a>
                                    )}
                                    {q.email && (
                                        <a
                                            href={`mailto:${q.email}`}
                                            className="flex items-center gap-2 text-brand-charcoal hover:text-brand-forest break-all"
                                        >
                                            <Mail className="h-4 w-4 text-brand-moss flex-shrink-0" />
                                            {q.email}
                                        </a>
                                    )}
                                    {q.address && (
                                        <p className="flex items-start gap-2 text-brand-charcoal">
                                            <MapPin className="h-4 w-4 text-brand-moss mt-0.5 flex-shrink-0" />
                                            {q.address}
                                        </p>
                                    )}
                                </div>
                                {q.service && (
                                    <div className="border-t border-brand-line pt-3">
                                        <p className="overline mb-1">Service</p>
                                        <p className="text-sm text-brand-charcoal">
                                            {q.service}
                                        </p>
                                    </div>
                                )}
                                {q.message && (
                                    <div className="border-t border-brand-line pt-3">
                                        <p className="overline mb-1">Message</p>
                                        <p className="text-sm text-brand-stone leading-relaxed">
                                            {q.message}
                                        </p>
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

/* ---------- Landing Page ---------- */
const Landing = () => (
    <>
        <Nav />
        <main>
            <Hero />
            <StandardServices />
            <AdditionalServices />
            <Gallery />
            <Testimonials />
            <About />
            <QuoteForm />
            <CTAStrip />
        </main>
        <Footer />
        <FloatingCall />
    </>
);

/* ---------- App ---------- */
function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/admin" element={<AdminPage />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-center" />
        </div>
    );
}

export default App;
